import { Component, type ErrorInfo, type ReactNode } from 'react';
import { EmptyState } from '@/components/ui';
import { captureError } from '@/lib/analytics';
import { errorMessage } from '@/lib/api';

/**
 * A render exception below this shows a designed fallback with a way out instead of a blank
 * page. Give it a `key` that changes with the page (the pathname) so moving on clears it.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: unknown }> {
  override state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('Render failed', error, info.componentStack);
    captureError(error, {
      source: 'error_boundary',
      component_stack: info.componentStack?.slice(0, 2000) ?? null,
      path: window.location.pathname,
    });
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ padding: '40px 16px', maxWidth: 560, width: '100%', margin: '0 auto' }}>
        <EmptyState
          title="This page hit a problem"
          body={`${errorMessage(this.state.error)}. Reloading usually fixes it; your work is saved.`}
          action={
            <button
              type="button"
              className="btn btn-primary hv"
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
          }
        />
      </div>
    );
  }
}
